import React, { type MutableRefObject, useState } from 'react';
import { type IPriceValueTranslationData } from '@secretescapes/cauldron';
import {
  getFormattedPriceWithBookingFee,
  getNavLeadPriceLabelling,
  getNavTotalPriceLabelling,
} from '@utils/pricing.utils';
import { useAppState } from '@utils/useAppState';
import { useApplicationState } from '@utils/useApplicationState';
import { BullfinchFeatureFlags, useFeatureFlag } from '@utils/useFeatureFlag';
import { useServerState } from '@utils/useServerState';
import { useTranslations } from '@utils/useTranslations';
import { useScroll } from '../useScroll';
import { useNavigationObserver } from './IntersectionObservers';
import { Container, PricingCtaContainer, StyledPriceValue } from './Navigation.styles';
import { NavigationCtaButton } from './NavigationCtaButton/NavigationCtaButton';
import { NavPricesContainer } from './PricesContainer/PricesContainer';
import { TableOfContents } from './TableOfContents';
import { type CustomRef, SectionId } from './utils';

export interface NavigationProps {
  stickyRef?: MutableRefObject<HTMLDivElement | null>;
  sectionRefs?: MutableRefObject<CustomRef[]>;
  onCtaClick: () => void;
}

export const Navigation = ({
  stickyRef = { current: null },
  sectionRefs = { current: [] },
  onCtaClick,
}: NavigationProps) => {
  const { currency, isMobile, territoryLocale } = useApplicationState();
  const { showPricingAndAvailability, pricingRules, finalRates } = useAppState();
  const { sale } = useServerState();
  const { translations } = useTranslations();

  const bookingFeesEnabled = useFeatureFlag(BullfinchFeatureFlags.bookingFeesEnabled);

  const { prices } = sale ?? {};

  const { leadRate, rackRate, discount, discountTooltip, withFlights } = prices ?? {};

  const emptyTranslationData: IPriceValueTranslationData = {
    priceValueHint: '',
    leadRateTooltip: '',
    priceValuePrefixLabel: '',
    priceValueSuffixLabel: '',
  };

  const priceTranslationData = finalRates.show
    ? getNavTotalPriceLabelling({
        unitPrice: finalRates.cheapestFinalRatePerRoomPerNight,
        numberOfTravellers: finalRates.numberOfTravellers,
        numberOfNights: finalRates.numberOfNights,
        flightsSelected: finalRates.flightsSelected,
        translations,
      })
    : getNavLeadPriceLabelling({
        pricing: prices,
        flightsSelected: finalRates.flightsSelected,
        translations,
      });

  const leadPricingRules = {
    showDiscount: finalRates.flightsSelected ? false : pricingRules.showDiscount && !isMobile,
    showRackRate: finalRates.flightsSelected ? false : pricingRules.showRackRate,
  };

  // Apply booking fee if HO and FF on
  const leadPricing = {
    leadRate: finalRates.flightsSelected
      ? (withFlights?.forDisplay ?? '')
      : bookingFeesEnabled
        ? getFormattedPriceWithBookingFee(leadRate?.unit ?? 0, currency, territoryLocale)
        : (leadRate?.forDisplay ?? ''),
    rackRate: finalRates.flightsSelected ? '' : (rackRate?.forDisplay ?? ''),
  };

  const [activeNavItem, setActiveNavItem] = useState<SectionId>(SectionId.Photos);

  const { scrollToSection } = useScroll(stickyRef);

  useNavigationObserver(stickyRef, sectionRefs, setActiveNavItem);

  const handleItemClick = (ref: CustomRef) => {
    scrollToSection(ref);
  };

  const showPricingAndCta =
    (activeNavItem !== SectionId.Offers && finalRates.show) || !finalRates.show;

  return (
    <Container isMobile={!!isMobile}>
      {!isMobile && (
        <TableOfContents
          onItemClick={handleItemClick}
          items={sectionRefs.current}
          activeItem={activeNavItem}
        />
      )}
      <PricingCtaContainer isMobile={!!isMobile} animationTrigger={showPricingAndCta}>
        {showPricingAndAvailability &&
          (finalRates.show && finalRates.cheapestFinalRate ? (
            <StyledPriceValue
              translationData={
                finalRates.cheapestFinalRate ? priceTranslationData : emptyTranslationData
              }
              leadRate={finalRates.cheapestFinalRate ?? ''}
              rackRate=""
              showRackRate={false}
              showTooltip={false}
            />
          ) : (
            <NavPricesContainer
              rules={leadPricingRules}
              pricing={leadPricing}
              discount={{ price: `${discount}`, tooltip: discountTooltip ?? '' }}
              translationData={priceTranslationData}
            />
          ))}
        <NavigationCtaButton
          showPricingAndAvailability={showPricingAndAvailability}
          onCtaClick={onCtaClick}
          showFinalRates={finalRates.show}
        />
      </PricingCtaContainer>
    </Container>
  );
};
